import * as amplitude from '@amplitude/analytics-browser';
import { isEmpty } from 'lodash';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

/**
 * Styling Guide For Amplitude Events:
 *
 * Case 1: Event Specific To A Page / Tab
 * Tab / Page - Event Name
 * Examples: Gifting - View Analytics, Campaigns - View Campaigns, Talent Card - offer custom rate
 *
 * Case 2: General Events
 * General - Event Name
 * Examples: General - Viewed Profile, General - Clicked Button, General - Issue Discount Code
 *
 *
 * How to call amplitude events:
 * window.__ADD_EVENT__('Event Name', { keys: and, values: for, context: here });
 */

/*
  While accessing the redux store directly is not recommended, we do it here because we want to access
  the user object globally without requiring it to be passed down through props. Please do not replicate
  this pattern elsewhere in the codebase unless you are sure you need to.
*/
import {
  isSimulatingUser,
  isBrand,
  isCreator,
  isManager,
  isShopper,
  isLoggedIn,
  getBrandSubscriptionBundle,
  getBrandWinningMerchant,
  getShopifyIntegration,
  getBrandSubscriptionInvoices,
  isEUUser
} from './user_helpers';
import { getUserTierDisplay } from './tier_helpers';
import { isSubscribedToFeature } from './subscription_helpers';
import { cleanBrandTier } from './brand_tier_helpers';
import { isStaging } from './helpers';

const LOG_EVENTS = false;

export const resetAmplitude = () => {
  // Reset amplitude instance
  amplitude.reset();
};

const getSampleRate = user => {
  if (!window.__IS_PROD__) {
    return 1; // record everything in dev
  } else if (isShopper(user)) {
    return 0.05; // record 5% of shoppers
  } else if (isCreator(user) || isBrand(user)) {
    return 0.02; // record 2% of creators and brands
  }

  return 0.01; // fallback to 1% for any other case
};

export const initializeAmplitude = user => {
  try {
    // Reset any existing instance first
    resetAmplitude();

    // Ensure we only do this once per page load
    if (!isAmplitudeTrackingEnabled(user)) return;

    // Initialize Session Replay
    const sessionReplayTracking = sessionReplayPlugin({
      sampleRate: getSampleRate(user),
      ...(isEUUser(user) ? { serverZone: 'EU' } : {}) // For GDPR compliance
    });
    amplitude.add(sessionReplayTracking);

    amplitude.init(
      isBrand(user)
        ? process.env.REACT_APP_AMPLITUDE_KEY_2025
        : isLoggedIn(user) && (isCreator(user) || isManager(user))
        ? process.env.REACT_APP_AMPLITUDE_KEY_2025_CREATORS
        : process.env.REACT_APP_AMPLITUDE_KEY_2025_SHOPPERS,
      null,
      {
        includeReferrer: true,
        includeUtm: true,
        includeGclid: true,
        defaultTracking: {
          pageViews: true,
          sessions: true
        },
        autocapture: {
          elementInteractions: true
        }
      }
    );
  } catch (e) {
    console.error('Error initializing Amplitude:', e);
    // Gracefully fail
  }
};

export const logAmplitudeEvent = (name, data = {}) => {
  if (!isAmplitudeTrackingEnabled()) {
    LOG_EVENTS && console.info('Untracked Amplitude Event:', name, data);
    return;
  } else {
    LOG_EVENTS && console.info('Tracked Amplitude Event:', name, data);
  }

  amplitude.logEvent(name, {
    ...data,
    brand: null,
    user: null, // Make sure not to pass the whole user object

    // Extra information to help debug certain cases
    ScreenResolution: `${window.screen.width}x${window.screen.height}` // ex: "1920x1080"
  });
};

export const isAmplitudeTrackingEnabled = user => {
  /*
    Since Amplitude charges on a per-user basis, we only want to track users who are brands for now,
    soon we will be able to track all logged in users.

    We must pass in the profile if we are doing this in the reducer, as you are not allowed to access
    the redux store directly in reducers.
  */

  // Do not track if we are simulating a user
  if (isSimulatingUser(user) && window.__IS_PROD__) return false;

  // If we are not on dev, track just for testing
  // if (window.__IS_DEV__) return true;
  if (window.__IS_DEV__) return false;

  // We are now tracking all users on production
  return true;
};

export const syncAmplitudeUserProfile = user => {
  if (!isAmplitudeTrackingEnabled(user)) return;

  try {
    // Set User_id to something readable
    const profile = user.profile;
    amplitude.setUserId(profile.brand?.name || profile.username);

    // Set other User Properties
    const identifyObj = new amplitude.Identify();
    identifyObj.set('User_id', profile.id);
    identifyObj.set('Email', profile.email);
    identifyObj.set('Name', profile.name);

    // User Type
    identifyObj.set('Is Brand', isBrand(user));
    identifyObj.set('Is Creator', isCreator(user));
    identifyObj.set('Is Manager', isManager(user));
    identifyObj.set('Is Shopper', isShopper(user));

    if (profile.brand?.id) {
      const brand = profile.brand;

      identifyObj.set('Brand', brand.name);
      identifyObj.set('Account Manager', brand.account_manager?.name || 'None');
      identifyObj.set('Package', getBrandSubscriptionBundle(user)?.title || '-');
      identifyObj.set('Network', getBrandWinningMerchant(user)?.source || '-');
      identifyObj.set('Shopify App', !!getShopifyIntegration(user).id);
      identifyObj.set('Shopify App - Tracking', !!getShopifyIntegration(user).allowAffiliateTracking);
      identifyObj.set('Program Report Tier', cleanBrandTier(brand.tier).currentTier.label);
      identifyObj.set('First Billing Date', getBrandSubscriptionInvoices(user)?.[0]?.createdAt || '-');
      identifyObj.set('Commission Rate', getBrandWinningMerchant(user) || '-');

      identifyObj.set('Module - Core', isSubscribedToFeature(user, 'CORE'));
      identifyObj.set('Module - Gifting', isSubscribedToFeature(user, 'GIFTING'));
      identifyObj.set('Module - Unlimited Gifting', isSubscribedToFeature(user, 'UNLIMITED_GIFTING'));
      identifyObj.set('Module - Social Listening', isSubscribedToFeature(user, 'SOCIAL_MENTIONS_FEED'));
      identifyObj.set('Module - Advanced Analytics', isSubscribedToFeature(user, 'ADVANCED_TALENT_ANALYTICS'));
      identifyObj.set('Module - Campaigns', isSubscribedToFeature(user, 'COLLABORATE'));
      identifyObj.set('Module - Campaign Management', isSubscribedToFeature(user, 'CAMPAIGN_MANAGEMENT'));
      identifyObj.set('Module - Communications', isSubscribedToFeature(user, 'UNLIMITED_CHAT'));
      identifyObj.set('Module - Opportunities', isSubscribedToFeature(user, 'OPPORTUNITIES'));
      identifyObj.set('Module - Sourcing Support', isSubscribedToFeature(user, 'EXPERT_SOURCING'));
      identifyObj.set('Module - Gifting Management', isSubscribedToFeature(user, 'GIFTING_MANAGEMENT'));

      if (!isEmpty(brand.stats)) {
        identifyObj.set('Icon Promoters', brand.stats.numIconsPromoting);
        identifyObj.set('trendsetter Promoters', brand.stats.numTrendsettersPromoting);
        identifyObj.set('Ambassador Promoters', brand.stats.numAmbassadorsPromoting);
        identifyObj.set('Promoters', brand.stats.numPromoting);

        identifyObj.set('Clicks', brand.stats.totalClicks);
        identifyObj.set('Monthly Clicks', brand.stats.monthlyClicks);
        identifyObj.set('Clicks DTC', brand.stats.totalClicksDTC);
        identifyObj.set('Monthly Clicks DTC', brand.stats.monthlyClicksDTC);

        identifyObj.set('Volume', brand.stats.totalVolume);
        identifyObj.set('Monthly Volume', brand.stats.monthlyVolume);
        identifyObj.set('Volume DTC', brand.stats.totalVolumeDTC);
        identifyObj.set('Monthly Volume DTC', brand.stats.monthlyVolumeDTC);

        identifyObj.set('Commissions', brand.stats.totalCommissions);
        identifyObj.set('Monthly Commissions', brand.stats.monthlyCommissions);
        identifyObj.set('Commissions DTC', brand.stats.totalCommissionsDTC);
        identifyObj.set('Monthly Commissions DTC', brand.stats.monthlyCommissionsDTC);

        identifyObj.set('Orders', brand.stats.totalOrders);
        identifyObj.set('Monthly Orders', brand.stats.monthlyOrders);
        identifyObj.set('Orders DTC', brand.stats.totalOrdersDTC);
        identifyObj.set('Monthly Orders DTC', brand.stats.monthlyOrdersDTC);
      }
    } else if (profile.isShopper) {
      // Nothing for now
    } else {
      identifyObj.set('Username', profile.username);
      identifyObj.set('Tier', getUserTierDisplay(profile.tier.tier));
      identifyObj.set('Best Tier', getUserTierDisplay(profile.tier.bestTier));
      identifyObj.set('True Score', profile.tier.trueScore);
      identifyObj.set('Account Manager', profile.account_manager?.name || 'None');
    }

    LOG_EVENTS && console.info('Syncing Amplitude User Profile:', identifyObj);
    amplitude.identify(identifyObj);
  } catch (e) {
    console.error('Error syncing Amplitude user profile:', e);
    // Gracefully fail
  }
};
